import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui/buttons/Button';
import { formatCurrency } from '~/utils';
import { PackedItemsList } from '~/pages/order-view/sections/PackedItemsList';
import { computePackingListDiff } from '~/pages/order-view/sections/packingListDiff';
import type { OrderShipmentForBuyer } from '~/api/order';

type PackingListModalProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  shipmentLabel: string;
  shipment: OrderShipmentForBuyer;
  currency?: string;
}>;

/** Shows a single shipment's packing list in a modal, reusing the same PackedItemsList renderer used on the seller-side packing list view. */
export function PackingListModal({
  isOpen,
  onClose,
  shipmentLabel,
  shipment,
  currency = 'USD',
}: PackingListModalProps) {
  const { t } = useTranslation('order');

  const orderItemByPplId = new Map(
    shipment.items.map((item) => [item.platformProductsLengths.id, item])
  );
  const diffRows = computePackingListDiff(
    shipment.items,
    shipment.packingListItems
  );
  const diffByPplId = new Map(diffRows.map((row) => [row.pplId, row]));
  const packedTotal = shipment.packingListItems.reduce(
    (sum, item) => sum + item.price.amount * item.quantity.value,
    0
  );
  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-2xl">
      <div className="bg-neut-0 rounded-lg overflow-hidden">
        <div className="p-6 border-b border-neut-100">
          <h2 className="text-neut-prim font-semibold">
            {t('order.shipments.modal_title', { shipmentLabel })}
          </h2>
        </div>

        {shipment.packingListItems.length === 0 ? (
          <p className="p-6 text-sm text-neut-sec">
            {t('order.shipments.no_packing_list')}
          </p>
        ) : (
          <PackedItemsList
            packingList={{ items: shipment.packingListItems }}
            orderItemByPplId={orderItemByPplId}
            diffByPplId={diffByPplId}
          />
        )}

        <div className="p-4 border-t border-neut-100 flex items-center justify-between gap-4">
          {shipment.packingListItems.length > 0 ? (
            <div className="rounded-xl border border-neut-200 bg-neut-0 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-neut-sec">
                {t('order.packing_list.packed_total')}
              </p>
              <p className="mt-1 text-lg font-semibold text-neut-prim">
                {formatCurrency(packedTotal, currency)}
              </p>
            </div>
          ) : (
            <div />
          )}
          <Button size="sm" width="auto" variant="secondary" onClick={onClose}>
            {t('order.shipments.close_button')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
